import React, { useContext, useEffect, useState } from "react";
import styled from "styled-components";
import Loader from "../loader/Loader";
import moment from "moment";
import pin from "../../../assets/icons/pin.svg";
import { baseConfig } from "../../../axiosConfig";
import { Context } from "../../../contexts/Store";
import pendingIcon from "../../../assets/icons/status-pending-icon.svg";
import submitIcon from "../../../assets/icons/status-paid-icon.svg";
import lockIcon from "../../../assets/icons/close.png";
import pdfIcon from "../../../assets/icons/pdf-icon.svg";
import returnIcon from "../../../assets/icons/status-due-icon.svg";
import removeIcon from "../../../assets/icons/remove-icon.svg";
import { useLocation, useNavigate } from "react-router-dom";
import SubmitAssignment from "../../screens/assignments/SubmitAssignment";


const getStatusIcon = (status) => {
    switch(status) {
        case "Active": return pendingIcon;
        case "Submitted": return submitIcon;
        case "Closed": return lockIcon;
        case "Returned": return returnIcon;
        default: return pendingIcon;
    }
};

const getStatusStyle = (status) => {
    switch(status) {
        case "Active":
            return {
                background: "#ffecd9",
                color: "#ffa143"
            };
        case "Submitted":
            return {
                background: "#d4f4d4",
                color: "#4caf50"
            };
        case "Closed":
            return {
                background: "#ffebee",
                color: "#f44336"
            };
        case "Returned":
            return {
                background: "#fff3e0",
                color: "#ff9800"
            };
        default:
            return {
                background: "#ffecd9",
                color: "#ffa143"
            };
    }
};

const renderFilePreview = (file) => {
      if (!file) return null;
      return (
        <UploadedFilePreview target="_blank" rel="noopener noreferrer" href={file}>
          <FileIcon src={pdfIcon} />
          <FileDetails>
            <UploadedFileName>Question file</UploadedFileName>
          </FileDetails>
        </UploadedFilePreview>
      );
    };

export default function OngoingAssignment({ data, setSinglePage, refreshApi }) {
    const [isLoading, setLoading] = useState(false);
    const { state } = useContext(Context);
    const location = useLocation();
    const navigate = useNavigate();
    
    const search_path = location?.search;
    const classroom_id = new URLSearchParams(search_path).get("c");
    
    const statusStyle = getStatusStyle(data?.status || "Active");

    

    return (
        <>
            <MainContainer>
                {!isLoading ? (
                    <>
                        {/* Assignment Header */}
                        <AssignmentTitleText>
                            {data?.data?.title || data?.title}
                        </AssignmentTitleText>
                        
                        <StatusDiv>
                            <AssignmentDetails>
                                <TotalQuestions>
                                    Total Questions: {data?.data?.total_questions || data?.total_questions}
                                </TotalQuestions>
                                {data?.data.question_type === "grade" &&
                                <TotalQuestions>
                                    Total Mark: {data?.data?.total_mark || data?.total_mark}
                                </TotalQuestions>
                                }
                                {(data?.data?.deadline || data?.deadline) && (
                                    <TotalQuestions>
                                        Deadline: {moment(data?.data?.deadline || data?.deadline).format("DD-MM-YYYY")}
                                    </TotalQuestions>
                                )}
                                
                            </AssignmentDetails>
                            
                            <StatusButton style={statusStyle}>
                                <StatusIcon src={getStatusIcon(data?.status || "Active")} alt="icon" />
                                <StatusText style={{ color: statusStyle.color }}>
                                    {data?.status || "Active"}
                                </StatusText>
                            </StatusButton>
                            {data.data.question_file && renderFilePreview(data.data.question_file)}
                        </StatusDiv>

                        {/* Main Content - Delegated to SubmitAssignment */}
                        <ContentContainer>
                            <SubmitAssignment 
                                data={data} 
                                refreshApi={refreshApi}
                                setSinglePage={setSinglePage}
                            />
                        </ContentContainer>
                    </>
                ) : (
                    <Loader />
                )}
            </MainContainer>
        </>
    );
}

// Styled Components
const MainContainer = styled.div`
    background: #ffffff;
    box-sizing: border-box;
    margin-top: 32px;
    height: calc(100vh - 110px);
    user-select: text !important;
    display: flex;
    flex-direction: column;
`;

const AssignmentTitleText = styled.h6`
    font-size: 24px;
    font-weight: 500;
    color: #010101;
    line-height: 1.5rem;
    user-select: text !important;
    padding: 0 20px;
    margin-bottom: 10px;
`;

const StatusDiv = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-top: 14px;
    padding: 0 20px;
    margin-bottom: 20px;
`;

const AssignmentDetails = styled.div`
    display: grid;
    grid-template-columns: 1fr 1fr 1fr;
    width: 60%;
    gap: 20px;
    
    @media (max-width: 768px) {
        grid-template-columns: 1fr;
        width: 70%;
        gap: 5px;
    }
`;

const TotalQuestions = styled.span`
    display: inline-block;
    font-size: 14px;
    color: #9d9d9d;
    white-space: nowrap;
`;

const UploadedFilePreview = styled.a`
  display: flex;
  text-decoration: none;
  align-items: center;
  background: #f8f9fa;
  border: 1px solid #e0e0e0;
  border-radius: 8px;
  padding: 10px 10px;
  min-width: 150px;
  transition: background 0.2s ease-in-out;

  &:hover {
    background: #98cbfeff;
  }
`;

const FileDetails = styled.div`
  flex: 1;
  margin-left: 8px;
`;

const UploadedFileName = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: #333;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 120px;
`;

const StatusButton = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 8px 12px;
    box-sizing: border-box;
    border-radius: 63px;
    transition: all 0.3s ease;
    border: 1px solid rgba(0,0,0,0.1);
`;

const StatusIcon = styled.img`
    width: 12px;
    display: block;
    object-fit: cover;
    margin-right: 6px;
`;

const StatusText = styled.p`
    font-size: 13px;
    font-weight: 500;
    margin: 0;
`;

const ContentContainer = styled.div`
    flex: 1;
    overflow: hidden;
    display: flex;
    flex-direction: column;
`;

// Legacy styled components (keeping for compatibility, but they're not used anymore)
const TextDiv = styled.div``;
const PathText = styled.span`
    display: inline-block;
    font-size: 14px;
    color: #9d9d9d;
    cursor: pointer;
    margin-bottom: 30px;
    &:hover {
        text-decoration: underline;
    }
`;
const Slash = styled.span`
    display: inline-block;
    font-size: 14px;
    color: #9d9d9d;
`;
const PathTextBlack = styled.span`
    color: #000;
    display: inline-block;
    font-size: 14px;
`;
const UpdatedDetails = styled.div`
    display: flex;
    align-items: center;
    margin-bottom: 12px;
    margin-top: 10px;
    &:last-child {
        margin-bottom: 0;
    }
`;
const ImageTag = styled.img`
    margin-right: 10px;
    width: 13px;
`;
const TextTag = styled.h6`
    font-size: 13px;
    color: #404040;
    display: flex;
    align-items: center;
`;
const ScrollContainer = styled.div`
    margin-top: 30px;
`;
const InstructionsText = styled.p`
    text-transform: uppercase;
    font-size: 15px;
    color: #9d9d9d;
    font-weight: bold;
    margin-top: 10px;
`;
const InstructionDiv = styled.div`
    padding: 0 20px; 
    list-style: inside;
    margin-top: 10px;
`;
const InstructionText = styled.p`
    font-size: 16px;
    color: #404040;
`;
const DetailedInstructions = styled.pre`
    font-family: inherit;
    margin-top: 15px;
    color: #404040;
    font-size: 1rem;
    width: 98%;
    line-height: 1.5em;
    white-space: pre-wrap;
    word-wrap: break-word;
`;
const SubmissionSection = styled.div`
    padding: 30px 0;
`;
const SubmissionText = styled.p`
    text-transform: uppercase;
    font-size: 16px;
    color: #9d9d9d;
`;
const Icon = styled.img`
    display: inline-block;
    width: 14px;
    object-fit: contain;
`;
const SubmittingContainer = styled.label`
    border: 1px dashed #9d9d9d;
    box-sizing: border-box;
    border-radius: 10px;
    display: flex;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    padding: 30px 0;
    cursor: pointer;
`;
const UploadText = styled.p`
    color: #c1c1c1;
    font-size: 14px;
    margin-bottom: 10px;
`;
const UploadButton = styled.label`
    padding: 12px 10px;
    border-radius: 8px;
    background: #15bf81;
    display: flex;
    align-items: center;
    cursor: pointer;
`;
const UploadIcon = styled.img`
    width: 25px;
    height: 20px;
    object-fit: contain;
`;
const UploadDiv = styled.div`
    display: flex;
    align-items: center;
`;
const UploadButtonText = styled.p`
    margin-left: 8px;
    font-size: 14px;
    font-weight: 500;
    color: #fff;
    margin-bottom: -2px;
`;
const SubmittedContainer = styled.div`
    padding: 25px;
    border: 1px solid #9d9d9d;
    box-sizing: border-box;
    display: flex;
    flex-direction: column;
    align-items: center;
    border-radius: 10px;
    overflow-y: scroll;
    &::-webkit-scrollbar {
        width: 2px;
    }
    &::-webkit-scrollbar-track {
        background-color: transparent;
    }
    ::-webkit-scrollbar-thumb {
        background-color: #d6dee1;
        border-radius: 20px;
    }
`;
const FileContainer = styled.div`
    display: flex;
    justify-content: space-between;
    align-items: center;
    width: 100%;
`;
const FileContainerLeft = styled.div`
    display: flex;
    align-items: center;
`;
const FileIcon = styled.img`
    width: 17px;
    height: 17px;
    object-fit: contain;
    margin-right: 8px;
`;
const FileName = styled.h6`
    font-size: 16px;
    color: #404040;
    text-transform: capitalize;
`;
const FileContainerRight = styled.div``;
const RemoveIcon = styled.img`
    width: 17px;
    height: 17px;
    object-fit: contain;
    cursor: pointer;
`;
const SubmitButtonContainer = styled.div`
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 20px;
`;
const SubmitButton = styled.button`
    display: inline-block;
    padding: 12px 20px;
    background: #15bf81;
    border-radius: 6px;
    font-size: 16px;
    font-weight: 500;
    letter-spacing: 0.05rem;
    color: #fff;
    cursor: pointer;
`;
const QuestionsWrapper = styled.div`
    padding: 15px;
    background: #f7f7f7;
    border-radius: 10px;
`;
const QuestionsText = styled.h6`
    color: #404040;
    font-size: 1rem;
    margin-top: 10px;
    padding: 10px 0;
    text-transform: uppercase;
`;
const DownloadButton = styled.button`
    background: rgb(82, 47, 145);
    border-radius: 4px;
    padding: 8px 16px;
    gap: 10px;
    height: 40px;
    display: flex;
    align-items: center;
    box-sizing: border-box;
    cursor: pointer;
    color: #fff;
    &:hover {
        opacity: 0.96;
    }
`;
const DownloadButtonContainer = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    margin-top: 20px;
`;